import './ChargerManagement.css';
import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
// Components
import ChargerTableItem from './component/ChargerTableItem';
import ChargerAppendSideModal from './component/ChargerAppendSideModal';
import ChargerDeleteModal from './component/ChargerDeleteModal';
import ChargerDetailModal from './component/ChargerDetailModal';
import ChargerUpdateSideModal from './component/ChargerUpdateSideModal';
import StationSearchModal from './component/StationSearchModal';
import SearchBox from '../../components/SearchBox/SearchBox';
import Pagination from '../../components/Pagination/Pagination';
import { SkeletonTable } from '../../components/Skeleton';
// Icons
import ExcelIcon from '../../assets/icons/excel.svg?react';
import PlusIcon from '../../assets/icons/plus.svg?react';
// Hooks
import useChargersSimple from '../../hooks/useChargersSimple';
import { useQueryClient } from '@tanstack/react-query';
import { chargerQueryKeys } from '../../hooks/queries/chargerQueries';

const ChargerManagement = () => {
  // Refs
  const selectedFilterRef = useRef('');
  const searchValueRef = useRef('');
  // States
  const [canSearch, setCanSearch] = useState(false);
  const [keywordInput, setKeywordInput] = useState(''); // 입력 중인 키워드 (로컬 state)
  const [deleteModalOn, setDeleteModalOn] = useState(false);
  const [editModalOn, setEditModalOn] = useState(false);
  const [appendModalOn, setAppendModalOn] = useState(false);
  const [chargerDetailModal, setChargerDetailModal] = useState(null);
  const [target, setTarget] = useState(null);
  const [stationSearchModalOn, setStationSearchModalOn] = useState(false);
  const [selectedStation, setSelectedStation] = useState(null);
  
  // URL Params
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const pageSize = parseInt(searchParams.get('pageSize')) || 10;
  const searchQuery = searchParams.get('search') || '';
  const keyword = searchParams.get('Keyword') || '';
  
  // keywordInput을 URL의 keyword와 동기화 (초기값 설정)
  useEffect(() => {
    if (keyword) {
      setKeywordInput(keyword);
    } else {
      setKeywordInput('');
    }
  }, [keyword]);
  
  // Filter Params
  const cpoChargerId = searchParams.get('CpoChargerId') || '';
  const cpoStationId = searchParams.get('CpoStationId') || '';
  const drResourceId = searchParams.get('DrResourceId') || '';

  // React Query Client
  const queryClient = useQueryClient();

  // Query Params Memoization
  const queryParams = useMemo(() => {
    const params = {
      Page: currentPage,
      PageSize: pageSize,
    };
    
    // Keyword 파라미터 추가
    if (keyword) {
      params.Keyword = keyword;
    }
    
    // Search Params
    if (cpoChargerId) {
      params.CpoChargerId = cpoChargerId;
    } else if (cpoStationId) {
      params.CpoStationId = cpoStationId;
    } else if (drResourceId) {
      params.DrResourceId = drResourceId;
    } else if (searchQuery) {
      params.search = searchQuery;
    }
    
    return params;
  }, [currentPage, pageSize, keyword, searchQuery, cpoChargerId, cpoStationId, drResourceId]);

  // React Query
  const { 
    chargers, 
    getStation,
    getDrResource,
    totalCount,
    isLoading: chargersLoading, 
    error: chargersError,
    refetch: refetchChargers
  } = useChargersSimple(queryParams);

  // Invalidate Charger Queries
  const invalidateChargerQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: chargerQueryKeys.all });
  }, [queryClient]);

  // Modal Close Handlers
  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModalOn(false);
    setTarget(null);
  }, []);

  const handleCloseAppendModal = useCallback(() => {
    setAppendModalOn(false);
    setSelectedStation(null);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setEditModalOn(false);
    setSelectedStation(null);
  }, []);

  const handleCloseStationSearchModal = useCallback(() => {
    setStationSearchModalOn(false);
  }, []);

  const handleCloseChargerDetailModal = useCallback(() => {
    setChargerDetailModal(null);
  }, []);

  // Success Handlers
  const handleDeleteSuccess = useCallback(() => {
    invalidateChargerQueries();
    setTarget(null);
    setDeleteModalOn(false);
  }, [invalidateChargerQueries]);

  const handleAppendSuccess = useCallback(() => {
    invalidateChargerQueries();
    setAppendModalOn(false);
    setSelectedStation(null);
  }, [invalidateChargerQueries]);

  const handleEditSuccess = useCallback(() => {
    invalidateChargerQueries();
    setEditModalOn(false);
    setSelectedStation(null);
  }, [invalidateChargerQueries]);

  // Update Search Params
  const updateSearchParams = (newParams) => {
    const current = Object.fromEntries(searchParams.entries());
    setSearchParams({ ...current, ...newParams });
  };

  const handlePageChange = (page) => {
    updateSearchParams({ page: page.toString() });
  };

  const handleSearch = (query, filterType) => {
    // Initialize New Params
    const newParams = { page: '1' };
    
    // Set Filter Type
    if (filterType && query) {
      newParams[filterType] = query;
    } else if (query) {
      // Set Default Search Param
      newParams.search = query;
    }
    
    // Remove All Search Params and Set New Params
    const current = Object.fromEntries(searchParams.entries());
    const filteredParams = Object.fromEntries(
      Object.entries(current).filter(([key]) => 
        !['search', 'CpoChargerId', 'CpoStationId', 'DrResourceId'].includes(key)
      )
    );
    
    setSearchParams({ ...filteredParams, ...newParams });
  };

  // Keyword 입력 핸들러 (로컬 state만 업데이트)
  const handleKeywordChange = (e) => {
    setKeywordInput(e.target.value);
  };

  // Keyword 입력에서 Enter 키 처리 (쿼리 실행)
  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = e.target.value.trim();
      const current = Object.fromEntries(searchParams.entries());
      
      if (value && value.length >= 2) {
        setSearchParams({ ...current, Keyword: value, page: '1' });
      } else {
        // 빈 값이면 Keyword 파라미터 제거
        const { Keyword, ...rest } = current;
        setSearchParams({ ...rest, page: '1' });
      }
    }
  };


  const handleChargerDetail = useCallback((charger) => {
    setChargerDetailModal(charger);
  }, []);

  const handleDelete = useCallback((charger, stationInfo) => {
    setTarget({ charger, stationInfo });
    setDeleteModalOn(true);
  }, []);

  const handleEdit = useCallback((charger, stationInfo) => {
    setTarget({ charger, stationInfo });
    setEditModalOn(true);
  }, []);

  const handleStationSearch = useCallback(() => {
    setStationSearchModalOn(true);
  }, []);

  // Error Retry Handler
  const handleRetry = useCallback(async () => {
    await refetchChargers();
  }, [refetchChargers]);


  return (
    <div className="charger-management-container page-container">      
      <div className="charger-management-content">

        <div className="flex justify-between align-center mb-10">
          <div className="w-half flex">
            <input 
              type="text" 
              placeholder="키워드 검색 (2자 이상) - DR자원 이름, 충전소 이름, 충전소 식별자, 충천기 식별자" 
              className="input-primary flex-1 text-b2-400" 
              value={keywordInput}
              onChange={handleKeywordChange}
              onKeyDown={handleKeywordKeyDown}
            />
          </div>
          <div className="flex justify-end gap-5">
            <>
              {/* <SearchBox 
                placeholder="검색" 
                filters={[
                  {id: 'CpoChargerId', label: '충전기 식별자'}, 
                  {id: 'CpoStationId', label: '충전소 식별자'}, 
                  {id: 'DrResourceId', label: 'DR 자원'}
                ]} 
                onFilterChange={() => {}} 
                onSearch={handleSearch}
                defaultValue={cpoChargerId || cpoStationId || drResourceId || searchQuery}
                selectedFilterRef={selectedFilterRef}
                searchValueRef={searchValueRef}
                initialSelectedFilter={cpoChargerId ? 'CpoChargerId' : cpoStationId ? 'CpoStationId' : drResourceId ? 'DrResourceId' : ''}
                onSearchStateChange={setCanSearch}
              /> */}
              {/* <button 
                className={`btn plain btn-style-2 px-10 py-5 ${!canSearch ? 'opacity-50 cursor-not-allowed' : ''}`} 
                onClick={() => canSearch && handleSearch(searchValueRef.current, selectedFilterRef.current)}
                disabled={!canSearch}
              >
                검색
              </button> */}
              {/* <button className="btn big-btn btn-green-700 flex align-center gap-3" onClick={() => {}}><ExcelIcon /> 스프레드 시트 다운로드</button> */}
              <button className="btn big-btn btn-primary flex align-center gap-3" onClick={() => setAppendModalOn(true)}><PlusIcon /> EV 충전기 추가</button>
            </>
          </div>
        </div>

        <div className="primary-table">
        {chargersLoading && (
          <SkeletonTable rows={8} columns={8} />
        )}
          
          {chargersError && (
            <div className="error-container">
              <p className="text-b1-400 text-base">데이터를 불러오는데 실패했습니다</p>
            </div>
          )}
          
          {!chargersLoading && !chargersError && (
            <>
              <table>
                <thead>
                  <tr>
                    <th>번호</th>
                    <th>DR자원</th>
                    <th>충전소 식별자</th>
                    <th>충전소 이름</th>
                    <th>충전소 주소</th>
                    <th>충전기 식별자</th>
                    <th>용량(kWh)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {chargers.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="no-data">
                        <p className="text-b1-400 text-gray-500">충전기 데이터가 없습니다.</p>
                      </td>
                    </tr>
                  ) : (
                    chargers.map((charger, idx) => {
                      const uniqueId = charger.ChargerSysId || `temp-${currentPage}-${idx}`;
                      const displayIndex = (currentPage - 1) * pageSize + idx + 1;
                      return (
                        <ChargerTableItem 
                          key={`${uniqueId}-${currentPage}-${idx}`} 
                          charger={charger} 
                          idx={displayIndex} 
                          onChargerDetail={handleChargerDetail}
                          stationInfo={getStation(charger.StationSysId)}
                          drResourceInfo={getDrResource(charger.DrResourceSysId)}
                          isStationLoading={false}
                          onDelete={handleDelete}
                          onEdit={handleEdit}
                        />
                      );
                    })
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>
        {/* Pagination */}
        {!chargersLoading && !chargersError && (
          <div className="pagination-wrapper">
          <Pagination
            currentPage={currentPage}
              totalPages={Math.max(1, Math.ceil(totalCount / pageSize))}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {chargerDetailModal && (
        <ChargerDetailModal 
          onClose={handleCloseChargerDetailModal} 
          charger={chargerDetailModal} 
        />
      )}
      {appendModalOn && (
        <ChargerAppendSideModal 
          onClose={handleCloseAppendModal} 
          onSuccess={handleAppendSuccess} 
          onStationSearch={handleStationSearch} 
          selectedStation={selectedStation} 
          setSelectedStation={setSelectedStation} 
        />
      )}
      {editModalOn && (
        <ChargerUpdateSideModal 
          onClose={handleCloseEditModal} 
          target={target} 
          onSuccess={handleEditSuccess} 
          onStationSearch={handleStationSearch} 
          selectedStation={selectedStation} 
          setSelectedStation={setSelectedStation} 
        />
      )}
      {deleteModalOn && target && (
        <ChargerDeleteModal 
          onClose={handleCloseDeleteModal} 
          charger={target.charger} 
          stationInfo={target.stationInfo} 
          onDeleteSuccess={handleDeleteSuccess} 
        />
      )}
      {stationSearchModalOn && (
        <StationSearchModal 
          onClose={handleCloseStationSearchModal} 
          selectedStation={selectedStation} 
          onSelect={setSelectedStation} 
        />
      )}
    </div>
  );
};

export default ChargerManagement; 